// "Ask about this call": a question box under the transcript status line (docs/api/ASK.md). Answers come from the
// call's transcript only, through the provider the operator configured. History is per call and per media-access
// epoch, held in component state only: a live transcript update never clears it; a different call or an access
// change does. Nothing renders unless the server reports the feature usable for this person.
import type { Call } from '@bcio-voice/contracts';
import { type FormEvent, type KeyboardEvent, type Ref, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useWorkspace } from '../../state/workspace.tsx';
import { useAnnouncer } from '../../ui/Announcer.tsx';
import { ErrorPanel } from '../../ui/StatePanel.tsx';
import { isAbortError } from '../../lib/api.ts';
import {
  ASK_MAX_LENGTH,
  type AskEntry,
  AskClient,
  QUICK_QUESTIONS,
  UNREADABLE_ANSWER,
  announcement,
  answeredLine,
  askCapability,
  askErrorView,
  askReducer,
  askVisible,
  cleanQuestion,
  escapeClears,
  readAskAnswer,
} from './ask.ts';

// Tokens only, like every stylesheet here; React 19 hoists this once into <head>.
const CSS = `
.ask-panel { display: grid; gap: var(--space-3); padding: var(--space-3) var(--space-4); border: 1px solid var(--rule); border-radius: var(--radius-panel); background: var(--surface); max-width: 72ch; }
.ask-title { margin: 0; font-size: var(--text-body); font-weight: var(--wght-strong); }
.ask-quick { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: var(--space-2); }
.btn.ask-quick-btn { white-space: normal; text-align: center; line-height: var(--leading-meta); padding: var(--space-1) var(--space-3); }
.ask-form { margin: 0; }
.ask-results { display: grid; gap: 0; margin: 0; padding: 0; list-style: none; border-top: 1px solid var(--rule); }
.ask-entry { display: grid; gap: var(--space-1); padding: var(--space-3) 0; border-bottom: 1px solid var(--rule); }
.ask-question { margin: 0; font-weight: var(--wght-medium); overflow-wrap: anywhere; }
.ask-answer { margin: 0; font-size: var(--text-read); line-height: var(--leading-read); overflow-wrap: anywhere; }
.ask-notes { display: flex; flex-wrap: wrap; gap: var(--space-1) var(--space-3); margin: 0; }
@media (max-width: 767px) {
  .ask-panel { padding: var(--space-3); }
  .ask-quick { grid-template-columns: minmax(0, 1fr); }
  .btn.ask-quick-btn { min-height: 44px; }
  .ask-form .btn { min-height: 44px; }
}
`;

function AskStyles() {
  return <style href="sidetone-ask-panel" precedence="default">{CSS}</style>;
}

export interface AskPanelViewProps {
  entries: readonly AskEntry[];
  draft: string;
  onDraft: (text: string) => void;
  onAsk: (question: string) => void;
  onClear: () => void;
  onRetry: (id: string) => void;
  inputRef?: Ref<HTMLInputElement>;
}

function Entry({ entry, onRetry }: { entry: AskEntry; onRetry: (id: string) => void }) {
  return (
    <li className="ask-entry">
      <p className="ask-question"><span className="visually-hidden">Question: </span>{entry.question}</p>
      {entry.status === 'loading' ? (
        <p className="meta" role="status" aria-busy="true">Thinking</p>
      ) : entry.status === 'error' ? (
        <ErrorPanel error={entry.error} onRetry={() => onRetry(entry.id)} compact />
      ) : (
        <>
          <p className="ask-answer"><span className="visually-hidden">{entry.answer.state === 'no_answer' ? 'No answer: ' : 'Answer: '}</span>{entry.answer.answer}</p>
          <p className="meta ask-notes">
            <span>{answeredLine(entry.answer)}</span>
            {entry.answer.provisional ? <span className="tone-warn">Part of the transcript was still provisional</span> : null}
            {entry.answer.truncated ? <span>Based on the most recent part of the transcript</span> : null}
          </p>
        </>
      )}
    </li>
  );
}

/** The panel's markup for a given history; the container below owns state and the request. */
export function AskPanelView({ entries, draft, onDraft, onAsk, onClear, onRetry, inputRef }: AskPanelViewProps) {
  const busy = entries.some(e => e.status === 'loading');
  const ready = !busy && cleanQuestion(draft) !== '';
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (ready) onAsk(draft);
  };
  const keys = (event: KeyboardEvent<HTMLInputElement>) => {
    if (escapeClears(event.key, draft)) {
      event.preventDefault();
      onClear();
    }
  };
  return (
    <section className="ask-panel" aria-labelledby="ask-title" aria-busy={busy ? 'true' : undefined}>
      <AskStyles />
      <h4 id="ask-title" className="ask-title">Ask about this call</h4>
      <div className="ask-quick" role="group" aria-label="Quick questions">
        {QUICK_QUESTIONS.map(q => (
          <button key={q} type="button" className="btn ask-quick-btn" disabled={busy} onClick={() => onAsk(q)}>{q}</button>
        ))}
      </div>
      <form className="field ask-form" onSubmit={submit} noValidate>
        <label htmlFor="ask-input">Your question</label>
        <div className="input-with-action">
          <input
            id="ask-input"
            ref={inputRef}
            type="text"
            maxLength={ASK_MAX_LENGTH}
            autoComplete="off"
            enterKeyHint="send"
            aria-describedby="ask-hint"
            aria-disabled={busy ? 'true' : undefined}
            readOnly={busy}
            value={draft}
            onChange={e => onDraft(e.target.value)}
            onKeyDown={keys}
          />
          <button type="submit" className="btn btn-primary" disabled={!ready}>Ask</button>
        </div>
        <p className="field-hint" id="ask-hint">Answers use only this call's transcript. Enter asks, Escape clears.</p>
      </form>
      {entries.length ? (
        <ol className="ask-results" aria-label="Answers, newest first">
          {entries.map(e => <Entry key={e.id} entry={e} onRetry={onRetry} />)}
        </ol>
      ) : null}
    </section>
  );
}

export function AskPanel({ call }: { call: Pick<Call, 'id' | 'transcription_state'> }) {
  const ws = useWorkspace();
  const { announce } = useAnnouncer();
  const client = useMemo(() => new AskClient(ws.api), [ws.api]);
  // A call that was never transcribed has nothing to ask about.
  const visible = call.transcription_state !== 'off' && askVisible(askCapability(ws.config.value), ws.permissions, ws.mediaAccess.state === 'ready');
  // History belongs to one call under one media-access epoch; anything else starts empty.
  const scope = `${call.id}:${ws.mediaAccess.epoch}`;
  const [history, setHistory] = useState<{ scope: string; entries: AskEntry[] }>({ scope, entries: [] });
  const entries = history.scope === scope ? history.entries : [];
  const [draft, setDraft] = useState('');
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const sequence = useRef(0);
  const inFlight = useRef(new Map<string, AbortController>());
  const panel = useRef<HTMLInputElement | null>(null);

  const dispatch = useCallback((forScope: string, action: Parameters<typeof askReducer>[1]) => {
    setHistory(h => {
      const base = h.scope === forScope ? h.entries : [];
      if (h.scope !== forScope && action.type !== 'asked') return h;
      return { scope: forScope, entries: askReducer(base, action) };
    });
  }, []);

  // A different call or an access change cancels what is in flight; its reply is never shown.
  useEffect(() => () => {
    for (const c of inFlight.current.values()) c.abort();
    inFlight.current.clear();
  }, [scope]);

  const run = useCallback((id: string, question: string, forScope: string, callId: string) => {
    const controller = new AbortController();
    inFlight.current.set(id, controller);
    const settle = (action: Parameters<typeof askReducer>[1], entry: AskEntry) => {
      if (scopeRef.current !== forScope || controller.signal.aborted) return;
      dispatch(forScope, action);
      const words = announcement(entry);
      if (words) announce(words);
      const input = panel.current;
      if (input && (document.activeElement === document.body || document.activeElement === null)) input.focus();
    };
    client.ask(callId, question, controller.signal).then(
      body => {
        const answer = readAskAnswer(body);
        if (answer) settle({ type: 'answered', id, answer }, { id, question, status: 'answered', answer });
        else settle({ type: 'failed', id, error: UNREADABLE_ANSWER }, { id, question, status: 'error', error: UNREADABLE_ANSWER });
      },
      error => {
        if (isAbortError(error)) return;
        const view = askErrorView(error);
        settle({ type: 'failed', id, error: view }, { id, question, status: 'error', error: view });
      },
    ).finally(() => {
      if (inFlight.current.get(id) === controller) inFlight.current.delete(id);
    });
  }, [announce, client, dispatch]);

  const busy = entries.some(e => e.status === 'loading');
  const ask = (text: string) => {
    const question = cleanQuestion(text);
    if (!question || busy) return;
    const id = `ask-${++sequence.current}`;
    dispatch(scope, { type: 'asked', id, question });
    setDraft('');
    run(id, question, scope, call.id);
  };
  const retry = (id: string) => {
    const entry = entries.find(e => e.id === id);
    if (!entry || busy) return;
    dispatch(scope, { type: 'retried', id });
    run(id, entry.question, scope, call.id);
  };

  if (!visible) return null;
  return <AskPanelView entries={entries} draft={draft} onDraft={setDraft} onAsk={ask} onClear={() => setDraft('')} onRetry={retry} inputRef={panel} />;
}
